import { Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { percentToTimestamp, useTimelineDrag } from '../../../hooks/useTimelineDrag';
import { ChapterTimelineEvent, Timeline } from '../../../parser';

export const ChaptersTrack = ({
    timeline,
    duration,
    isActive,
    onClick
}: {
    timeline: Timeline;
    duration: Duration;
    isActive: boolean;
    onClick: (event: ChapterTimelineEvent) => void;
}) => {
    const [chapters, setChapters] = useState<ChapterTimelineEvent[]>([]);

    useEffect(() => {
        if (!timeline?.chapters || duration.toMillis() === 0) {
            setChapters([]);
            return;
        }

        setChapters(timeline.chapters);
    }, [timeline?.chapters, duration]);

    const drag = useTimelineDrag<ChapterTimelineEvent>(
        (chapter, position) => {
            // Video time works in seconds, so we need to round the timestamp to the nearest second
            chapter.timestamp = percentToTimestamp(duration, position);
            // Check if there are two chapters with the same timestamp
            const duplicate = timeline.chapters.find((c) => c.timestamp.equals(chapter.timestamp) && c !== chapter);
            if (!!duplicate) {
                if (chapter.timestamp.as('seconds') > 0) {
                    chapter.timestamp = chapter.timestamp.minus({ seconds: 1 });
                } else {
                    chapter.timestamp = chapter.timestamp.plus({ seconds: 1 });
                }
            }
            setChapters([...timeline.chapters.sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis())]);
        },
        (chapter) => {
            chapter.changed = true;
            timeline.changed = true;
            timeline.chapters = [...timeline.chapters];
        }
    );

    const onDragStart = (e: React.MouseEvent<HTMLButtonElement>, chapter: ChapterTimelineEvent) =>
        drag.begin(e, chapter, (chapter.timestamp.toMillis() / duration.toMillis()) * 100);

    return (
        <div className={`track track-chapters ${isActive ? 'track-active' : ''} row-start-4`}>
            {chapters.map((chapter, index) => {
                let width = 0;
                if (index === chapters.length - 1) {
                    width = ((duration.toMillis() - chapters[index].timestamp.toMillis()) / duration.toMillis()) * 100;
                } else {
                    width = ((chapters[index + 1].timestamp.toMillis() - chapters[index].timestamp.toMillis()) / duration.toMillis()) * 100;
                }
                return (
                    <div
                        key={index}
                        className={chapter.changed ? 'changed' : ''}
                        style={{ left: `${(chapter.timestamp.toMillis() / duration.toMillis()) * 100}%`, width: `${width}%` }}
                        onClick={() => onClick(chapter)}
                    >
                        <button onMouseDown={(event) => onDragStart(event, chapter)}></button>
                        <span>
                            {index + 1}. <strong>{chapter.title}</strong>
                        </span>
                    </div>
                );
            })}
        </div>
    );
};
